import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { smartPruneNumber } from '@/utils/number'
import defaultLogo from '@/assets/logo.png'
import egaLogoDark from '@/assets/mtx-ega-logo-darkmode.png'
import notoSansRegularUrl from '@/assets/fonts/NotoSans-Regular.ttf'
import notoSansBoldUrl from '@/assets/fonts/NotoSans-Bold.ttf'
import castingPropertyHelpMainDb from '@/constants/castingPropertyHelp.mainDb.json'
import castingPropertyHelpBenchmarkDb from '@/constants/castingPropertyHelp.benchmarkDb.json'
import { resolveCastingPropertyHelp } from '@/utils/castingPropertyHelp'

const HEADER_COLOR = [3, 147, 168]
const FONT_NAME = 'NotoSans'

let fontCache = null

function sanitizeFilename(name) {
  const base = pdfSafeText(name || 'predix-report')
    .replace(/[^\w\s-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80)
  return (base || 'predix-report').toLowerCase()
}

/**
 * Clean text for PDF while keeping Greek letters and common science symbols
 * (α, β, ±, °, ™, …). Requires the embedded Unicode font.
 */
function pdfSafeText(value) {
  let s = String(value == null ? '' : value)
  try {
    s = s.normalize('NFC')
  } catch (e) {
    /* ignore */
  }
  s = s
    .replace(/[\u00A0\u202F\u2007\u2009\u200A]/g, ' ') // nbsp / thin spaces
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '') // zero-width
    .replace(/[\u2010-\u2015\u2212]/g, '-') // dash variants → hyphen
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D\u2033]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\uFFFD/g, '')
  return s.replace(/[ \t\f\v]+/g, ' ').trim()
}

function formatValue(value) {
  const n = typeof value === 'number' ? value : parseFloat(value)
  if (Number.isFinite(n)) return pdfSafeText(smartPruneNumber(n))
  return value == null ? '-' : pdfSafeText(value)
}

function isAluminumName(name) {
  const s = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  return s === 'al' || s === 'al wt%' || s === 'al wt' || s === 'aluminum' || s === 'aluminium'
}

function formatCompositionValue(item) {
  const n = typeof item.value === 'number' ? item.value : parseFloat(item && item.value)
  if (!Number.isFinite(n)) return formatValue(item && item.value)
  if (isAluminumName(itemKey(item))) {
    return pdfSafeText(Number(n.toFixed(3)).toString())
  }
  return formatValue(n)
}

function itemKey(item) {
  return pdfSafeText((item && (item.key || item.name || item.title)) || '-')
}

const PROPERTY_HELP_MAP = {
  ...castingPropertyHelpBenchmarkDb,
  ...castingPropertyHelpMainDb,
}

function itemDescription(item) {
  const name = (item && (item.key || item.name || item.title)) || ''
  const help = resolveCastingPropertyHelp(PROPERTY_HELP_MAP, name)
  return pdfSafeText((help && help.description) || '-')
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Missing image src'))
      return
    }
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load logo'))
    img.src = src
  })
}

function imageToPngDataUrl(img) {
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth || img.width
  canvas.height = img.naturalHeight || img.height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)
  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  }
}

async function fetchFontBase64(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to load font: ${url}`)
  const buf = await res.arrayBuffer()
  const bytes = new Uint8Array(buf)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

async function getUnicodeFonts() {
  if (fontCache) return fontCache
  const [regular, bold] = await Promise.all([
    fetchFontBase64(notoSansRegularUrl),
    fetchFontBase64(notoSansBoldUrl),
  ])
  fontCache = { regular, bold }
  return fontCache
}

async function registerUnicodeFont(doc) {
  const fonts = await getUnicodeFonts()
  doc.addFileToVFS('NotoSans-Regular.ttf', fonts.regular)
  doc.addFileToVFS('NotoSans-Bold.ttf', fonts.bold)
  doc.addFont('NotoSans-Regular.ttf', FONT_NAME, 'normal')
  doc.addFont('NotoSans-Bold.ttf', FONT_NAME, 'bold')
  doc.setFont(FONT_NAME, 'normal')
}

/**
 * Build and download a PrediX report PDF grouped by category.
 * @param {{
 *   title: string,
 *   reportId?: string,
 *   groups: Array<{ cat: string, items: any[] }>,
 *   eyebrow?: string,
 *   useEgaLogo?: boolean,
 *   logoSrc?: string,
 * }} options
 */
export async function downloadPredixReportPdf({
  title,
  groups,
  eyebrow,
  useEgaLogo = false,
  logoSrc,
} = {}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
  await registerUnicodeFont(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 14
  const headerPadX = 14
  const headerPadY = 8
  const safeTitle = pdfSafeText(title || 'Untitled report')
  const categories = Array.isArray(groups) ? groups : []
  const attribution = pdfSafeText(
    eyebrow ||
      (useEgaLogo
        ? 'Generated by MetalNx PrediX™ and the EGA Database'
        : "Generated by MetalNx's PrediX™")
  )

  const resolvedLogoSrc = logoSrc || (useEgaLogo ? egaLogoDark : defaultLogo)
  let logoWidthMm = 0
  let logoHeightMm = 0
  let logoDataUrl = ''

  try {
    const img = await loadImage(resolvedLogoSrc)
    const packed = imageToPngDataUrl(img)
    logoDataUrl = packed.dataUrl
    logoHeightMm = useEgaLogo ? 9 : 10
    logoWidthMm = (packed.width / packed.height) * logoHeightMm
  } catch (e) {
    /* continue without logo */
  }

  const textX = logoDataUrl ? headerPadX + logoWidthMm + 3.5 : headerPadX
  const textMaxWidth = pageWidth - textX - headerPadX
  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(11)
  const titleLines = doc.splitTextToSize(safeTitle, textMaxWidth)
  const titleBlockHeight = Math.max(titleLines.length, 1) * 4.5
  const textBlockHeight = titleBlockHeight + 4
  const contentHeight = Math.max(logoHeightMm || 0, textBlockHeight)
  const headerHeight = headerPadY + contentHeight + headerPadY

  // Full-bleed brand header (top/left/right flush)
  doc.setFillColor(HEADER_COLOR[0], HEADER_COLOR[1], HEADER_COLOR[2])
  doc.rect(0, 0, pageWidth, headerHeight, 'F')

  const contentTop = headerPadY
  if (logoDataUrl) {
    const logoY = contentTop + Math.max((contentHeight - logoHeightMm) / 2, 0)
    doc.addImage(logoDataUrl, 'PNG', headerPadX, logoY, logoWidthMm, logoHeightMm)
  }

  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(11)
  doc.setTextColor(255, 255, 255)
  doc.text(titleLines, textX, contentTop + 4)

  const attributionY = contentTop + 4 + titleBlockHeight + 0.5
  doc.setFont(FONT_NAME, 'normal')
  doc.setFontSize(7)
  doc.setTextColor(255, 255, 255)
  doc.text(attribution, textX, attributionY)

  // Keep current spacing before first category/table
  let cursorY = headerHeight + 10

  if (!categories.length) {
    doc.setFont(FONT_NAME, 'normal')
    doc.setFontSize(11)
    doc.setTextColor(17, 24, 39)
    doc.text('This report has no items.', marginX, cursorY + 4)
  } else {
    categories.forEach((group) => {
      const pageH = doc.internal.pageSize.getHeight()
      if (cursorY > pageH - 40) {
        doc.addPage()
        cursorY = 16
      }

      const catName = pdfSafeText(group.cat || 'Other')
      const isComposition =
        catName === 'Alloy Composition' || catName === 'Composition'
      const isTwoColumnTable =
        isComposition || /supersaturated/i.test(catName)
      const nameHeader = isComposition
        ? 'Alloy Composition'
        : (/^alloy\b/i.test(catName) ? catName : `Alloy ${catName}`)
      const cardTitle = isComposition
        ? 'Alloy Composition'
        : (nameHeader.replace(/^Alloy\s+/i, '').trim() || nameHeader)
      const valueHeader = isComposition ? 'Value' : 'Predicted Value'

      doc.setFont(FONT_NAME, 'bold')
      doc.setFontSize(11)
      doc.setTextColor(17, 24, 39)
      doc.text(cardTitle, marginX, cursorY)
      cursorY += 3

      const rows = (group.items || []).map((item) => {
        const value = isComposition ? formatCompositionValue(item) : formatValue(item && item.value)
        const row = [itemKey(item), value]
        if (!isTwoColumnTable) row.splice(1, 0, itemDescription(item))
        return row
      })
      const head = isTwoColumnTable
        ? [[nameHeader, valueHeader]]
        : [[nameHeader, 'Description', valueHeader]]
      const emptyRow = isTwoColumnTable ? [['No items', '-']] : [['No items', '-', '-']]
      const columnStyles = isTwoColumnTable
        ? {
            0: { cellWidth: 233 },
            1: { cellWidth: 36, halign: 'center', fontStyle: 'bold', valign: 'middle' },
          }
        : {
            0: { cellWidth: 72 },
            1: { cellWidth: 161 },
            2: { cellWidth: 36, halign: 'center', fontStyle: 'bold', valign: 'middle' },
          }

      autoTable(doc, {
        startY: cursorY,
        margin: { left: marginX, right: marginX },
        head,
        body: rows.length ? rows : emptyRow,
        theme: 'grid',
        styles: {
          font: FONT_NAME,
          fontStyle: 'normal',
          fontSize: 8.5,
          cellPadding: 2.2,
          textColor: [31, 41, 55],
          lineColor: [229, 231, 235],
          lineWidth: 0.2,
          overflow: 'linebreak',
          valign: 'top',
        },
        headStyles: {
          font: FONT_NAME,
          fontStyle: 'bold',
          fillColor: HEADER_COLOR,
          textColor: [255, 255, 255],
          fontSize: 8,
          valign: 'middle',
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251],
        },
        columnStyles,
      })

      cursorY = (doc.lastAutoTable && doc.lastAutoTable.finalY
        ? doc.lastAutoTable.finalY
        : cursorY) + 10
    })
  }

  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i)
    const pageH = doc.internal.pageSize.getHeight()
    doc.setFont(FONT_NAME, 'normal')
    doc.setFontSize(8)
    doc.setTextColor(156, 163, 175)
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageH - 8, { align: 'center' })
  }

  const filename = `${sanitizeFilename(safeTitle)}.pdf`
  doc.save(filename)
  return filename
}
